import type { MailboxInfo } from "owa-client-types";
import { getAccountScopeUserSettings } from "owa-session-store";
import isConsumer from "owa-session-store/lib/utils/isConsumer";

// The prefix used for EASID (consumer/MSA) email addresses: outlook_<HexCID>@...
export const EASID_PREFIX = "outlook_";

/**
 * Determines whether the given email address is the signed-in user's EASID
 * (outlook_<HexCID>@...) by matching it against the account's HexCID. This is
 * consistent with the native account source list and getSenderDisplayName.
 * @param email The email/SMTP address to check. Returns false when empty (e.g. PST/no-SMTP accounts).
 * @param userSettings The account-scoped user settings containing the HexCID.
 * @returns true if the email is the account's EASID address; otherwise false.
 */
export function hasEasidPattern(email: string | null | undefined, userSettings: {
    HexCID?: string | null;
} | null | undefined): boolean {
    if (!email || !userSettings?.HexCID) {
        return false;
    }
    return email.toLowerCase().indexOf((EASID_PREFIX + userSettings.HexCID).toLowerCase()) === 0;
}

/**
 * Returns the MSA (consumer) account's user display name when its email address is
 * actually the EASID (outlook_<HexCID>@...), which callers should show in place of
 * the raw EASID address. In all other cases returns undefined so the caller can fall
 * back to the email/SMTP address (or another label).
 * @param email The account's email/SMTP address.
 * @param mailboxInfo The account's MailboxInfo, used to resolve consumer status and settings.
 * @returns The user display name to substitute, or undefined when no substitution applies.
 */
export function getMsaDisplayNameForEasidAddress(email: string | null | undefined, mailboxInfo: MailboxInfo): string | undefined {
    if (email &&
        isConsumer(undefined, mailboxInfo) &&
        hasEasidPattern(email, getAccountScopeUserSettings(mailboxInfo))) {
        return (getAccountScopeUserSettings(mailboxInfo)?.SessionSettings?.UserDisplayName || undefined);
    }
    return undefined;
}
